import React, { useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { GalleryModal, Typography } from '@hpi-sox/hpstellar-core';
import useThreeDTooltipProps from './useThreeDTooltipProps';
import loadable from '@loadable/component';

import useTrackCustomMetric from '../../../../hooks/useTrackCustomMetric';
import { getAnalyticsVideoName, getAddon, getGTMClickAttributes } from '../../../../metrics/metrics-helpers';

import withPageComponents from '../../../../page/components/with-page-components';
import AddOnPrice from '../../../../product/components/product-configurations/addon-price';
import useAddToCart from '../../../../hooks/useAddToCart';
import useProductPrice from '../../../../hooks/useProductPrice';
import useCartState from '../../../../hooks/useCartState';
import { Helpers } from '../../../../core/src/helpers';
import withPurchaseModal from '../../../withPurchaseModal';

import './image-modal.less';

const EMPTY_OBJECT = {};

const removeDimensions = orientation => {
    try {
        return orientation.replace(/\d+x\d+$/, '').trim();
    } catch (e) {
        return orientation;
    }
};

const getZoomedInUrlIfWiden = images =>
    Array.isArray(images)
        ? images.map(img => {
              let { url, type } = img || {};
              let isWidenImage = typeof url === 'string' && url.includes('widen.net') && /\/(jpg|png)\//gi.test(url);
              const { isVideo } = Helpers.getImageProps(type);

              return {
                  ...(img || {}),
                  ...(!isVideo && isWidenImage ? { url: Helpers.getCDNUrl(url, url, 1659) } : {}),
              };
          })
        : null;

/**
 * Uses zoomImages wherever available
 * @param {*} images
 */
const mapImages = (images, customSlideComp) => {
    const { defaultImages = [], zoomedFullImages = [] } = images || {};
    const isParallel = defaultImages?.length === zoomedFullImages?.length;
    let derivedZoomedFullImages;
    try {
        derivedZoomedFullImages = !isParallel ? getZoomedInUrlIfWiden(defaultImages) : null;
    } catch (e) {}

    let imagesToUse = isParallel
        ? zoomedFullImages
        : derivedZoomedFullImages?.length === defaultImages?.length
          ? derivedZoomedFullImages
          : defaultImages;

    imagesToUse = Array.isArray(imagesToUse) ? imagesToUse : [];
    const isUsingDefaultImages = imagesToUse === defaultImages;

    return imagesToUse.map((img, idx) => {
        if (img.orientation === '360 Viewer' && customSlideComp) {
            return {
                type: 'custom3D',
                customComp: customSlideComp({ url: img.url, alt: img.alt }),
                btnProps: { ...getGTMClickAttributes('linkClick', 'gallery', '3d') },
            };
        }
        if (img.type !== 'video') {
            return isUsingDefaultImages ? { ...img, isLowResolution: true } : img;
        } else if (img.url && /\.mp4(\?.*)?$/.test(img.url)) {
            return {
                ...img,
                videoProps: {
                    sources: [{ src: img.url, type: 'video/mp4' }],
                },
            };
        }
        return img;
    });
};

const getModalData = (product, images, currentPrice, ctoConfig, isReviewsPage, customSlideComp) => {
    if (product) {
        const {
            ctaText,
            ctoLink,
            customizeText,
            isOOS,
            currency,
            locale,
            deal,
            isComingSoonSku,
            translations,
            ...restProps
        } = currentPrice || {};
        const { name, sku, eol, ctaEol, ctaEolUrl, ctaAddToCart, ctaReplacementURL, product_type } = product;
        const eolCTAText = eol && (ctaEol || ctaAddToCart);
        const priceDifference = currentPrice.regularPrice - currentPrice.salePrice;
        const outOfStock = eol
            ? !(ctaEolUrl || ctaReplacementURL)
            : Helpers.isGiftCard(product)
              ? !!(ctoConfig || {}).error
              : !isComingSoonSku && isOOS;
        const price =
            currentPrice && !eol && !isReviewsPage
                ? {
                      ...restProps,
                      currencySymbol: '$',
                      regularPrice: currentPrice.regularPrice,
                      salePrice: currentPrice.salePrice,
                      productType: !restProps?.msrp ? product.product_type.toLowerCase() : undefined,
                      currency,
                      locale,
                      priceDifference,
                      hasActiveDeal: !!deal,
                      style: 'center',
                      translations,
                  }
                : EMPTY_OBJECT;
        return {
            data: {
                images: mapImages(images, customSlideComp),
                ratings: {
                    score: product.rating,
                    reviewsCount: product.numReviews,
                },
                title: name,
                price,
                sku: `Product # ${sku}`,
                outOfStock,
            },
            cta: {
                ctaText: eolCTAText || ctaText,
                ctoLink,
                //Gift cards on the PDP should not have customize text
                customizeText: product_type === 'CTO' && customizeText,
            },
        };
    }
    return EMPTY_OBJECT;
};

const GalleryModalWithPurchaseModal = withPurchaseModal(props => {
    const history = useHistory();
    const {
        // GalleryModal props
        className,
        isOpen,
        initialSlide,
        data,
        onModalClose,
        onSlideChange,
        gtmActions,
        translations,
        EmblaCarouselProps,
        thumbnailCarouselProps,
        threeDTooltipProps,
        threeDButtonProps,
        threeDZoomInBtnProps,
        threeDZoomOutBtnProps,
        threeDZoomResetBtnProps,

        // other props
        goToCTOPage,
        hideAddToCart,
        productInitial,
        isReviewsPage,
        totalPrice,

        // usePdpCta hook props
        ctaFunction,
    } = props || {};

    const { sku, product_type, eol, ctaReplacementURL, ctaAddToCart, ctaEolUrl, ctaEol, linkUrl } =
        productInitial || {};
    const eolLink = eol && (ctaEolUrl || ctaReplacementURL);

    const onAdd = !hideAddToCart
        ? e => {
              if (eol && eolLink) {
                  history.push(Helpers.getRelativePath(eolLink));
                  return;
              }
              if (isReviewsPage) {
                  history.push(Helpers.getRelativePath(linkUrl));
                  return;
              }
              if (product_type === 'CTO') {
                  goToCTOPage(productInitial, 1);
              } else {
                  ctaFunction(e);
              }
          }
        : undefined;

    const addToCartBtnProps = {
        onClick: onAdd,
        timeoutSuccess: !totalPrice?.isComingSoonSku,
    };

    const galleryModalProps = {
        className,
        isOpen,
        initialSlide,
        data,
        onModalClose,
        onSlideChange,
        gtmActions,
        translations,
        EmblaCarouselProps,
        thumbnailCarouselProps,
        threeDTooltipProps,
        threeDButtonProps,
        threeDZoomInBtnProps,
        threeDZoomOutBtnProps,
        threeDZoomResetBtnProps,
        addToCartBtnProps,
    };

    return <GalleryModal {...galleryModalProps} />;
});

export default withPageComponents(
    ({
        productInitial,
        images,
        getGtmActions,
        imageIndex,
        setImageIndex,
        ctoConfig,
        addOns,
        ctaProps,
        vanityUrl,
        has3D,
        device,
        width,
    }) => {
        let history = useHistory();
        const { sku, product_type, eol, ctaReplacementURL, ctaAddToCart, ctaEolUrl, ctaEol, linkUrl } = productInitial;
        const addonProduct = addOns && getAddon(sku, addOns);
        const { addToCart, goToCTOPage, launchCartFlyout } = useAddToCart(false, { withCTOConfigurations: true });
        const { cartId } = useCartState();
        const { prices } = useProductPrice([productInitial, ...(addonProduct ? [addonProduct] : [])], {
            withCTOConfigurations: true,
        });
        const productPrice = prices[sku];
        const eolLink = eol && (ctaEolUrl || ctaReplacementURL);
        const isOpen = imageIndex > -1;
        const { addText } = ctaProps || EMPTY_OBJECT;
        const isReviewsPage = vanityUrl.indexOf('reviews/') > -1;

        const threeDTooltipProps = useThreeDTooltipProps(width, device);

        return (
            <AddOnPrice sku={sku} price={productPrice}>
                {({ totalPrice }) => {
                    const modelViewerRef = useRef(null);
                    let ModelViewer = null;
                    let customSlideComp;
                    if (has3D) {
                        ModelViewer = loadable(() => Helpers.retryFunc(() => import('./model-viewer')));
                        ModelViewer.preload();
                        customSlideComp = React.useCallback(
                            ({ url, alt }) => <ModelViewer url={url} alt={alt} ref={modelViewerRef} />,
                            [ModelViewer],
                        );
                    }

                    const { data, cta } = getModalData(
                        productInitial,
                        images,
                        totalPrice,
                        ctoConfig,
                        isReviewsPage,
                        customSlideComp,
                    );
                    const { hideAddToCart } = productPrice;
                    const trackCustomMetric = useTrackCustomMetric();
                    const [currentIdx, setCurrentIdx] = useState(imageIndex);

                    useEffect(() => {
                        try {
                            const { type, url } = data.images[currentIdx];
                            if (isOpen && type === 'video' && url) {
                                trackCustomMetric('videoLoad', {
                                    videoName: getAnalyticsVideoName(url, currentIdx),
                                    videoDuration: 999,
                                });
                            }
                        } catch (e) {}
                    }, [currentIdx, isOpen]);

                    useEffect(() => {
                        window['useWindowSize_debounceTime'] = 0;
                    }, []);

                    const purchaseModalProps = {
                        product: productInitial,
                        productInitial,
                        goToCTOPage,
                        hideAddToCart,
                        isReviewsPage,
                        totalPrice,
                    };

                    return (
                        <GalleryModalWithPurchaseModal
                            key={`modalReset-${isOpen}`}
                            className={`imageModal${has3D ? ' with3D' : ''}`}
                            isOpen={isOpen}
                            initialSlide={Math.max(imageIndex, 0)}
                            data={{ ...data, titleMaxLines: has3D && 2 }}
                            onModalClose={() => setImageIndex(-1)}
                            onSlideChange={setCurrentIdx}
                            gtmActions={
                                getGtmActions &&
                                getGtmActions({
                                    prices,
                                    productInitial,
                                    cartId,
                                    selectedAddonSku: addonProduct && addonProduct.sku,
                                })
                            }
                            translations={{ addText: addText || cta.customizeText || cta.ctaText }}
                            EmblaCarouselProps={{
                                className: 'imageModal-carousel',
                                containerClassName: 'imageModal-carousel-container',
                            }}
                            thumbnailCarouselProps={{ columns: 'three' }}
                            threeDTooltipProps={threeDTooltipProps}
                            threeDButtonProps={{
                                children: '3D View',
                                ...getGTMClickAttributes('linkClick', 'gallery', '3d'),
                            }}
                            threeDZoomInBtnProps={{
                                onClick: () => {
                                    modelViewerRef.current.zoomIn();
                                },
                                disabled: false,
                            }}
                            threeDZoomOutBtnProps={{
                                onClick: () => {
                                    modelViewerRef.current.zoomOut();
                                },
                                disabled: false,
                            }}
                            threeDZoomResetBtnProps={{
                                onClick: () => {
                                    modelViewerRef.current.reset();
                                },
                                disabled: false,
                            }}
                            {...purchaseModalProps}
                        />
                    );
                }}
            </AddOnPrice>
        );
    },
    { components: ['productInitial'], addOns: true, device: true },
);
